import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { AuthUser } from '../shared/types'
import {
  authLogin,
  authLogout,
  authRefresh,
  authRegister,
  getAuthMe,
  setAuthToken,
} from '../shared/api/endpoints'

// ── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  /** True while the silent refresh check on mount is in flight. */
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** Re-fetch user data from /auth/me and update in-memory state (e.g. after plan upgrade). */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Loading skeleton ──────────────────────────────────────────────────────────
// Shown during the silent refresh on mount — PR-P5-03: never blank screen.

function AuthLoadingSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-700 border-t-primary-500" />
        <div className="space-y-2 text-center">
          <div className="h-3 w-32 animate-pulse rounded bg-neutral-800" />
          <div className="h-2 w-20 animate-pulse rounded bg-neutral-800/60" />
        </div>
      </div>
    </div>
  )
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  // Access token is kept only in memory — never written to localStorage.
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Prevent double-invocation in React.StrictMode (dev-only double-mount).
  const initialized = useRef(false)

  // PR-P5-03: On mount, silently attempt to refresh the access token using the
  // httpOnly cookie. If the cookie is valid, the user is restored without a
  // login screen. If it fails (no cookie / expired), the user is unauthenticated.
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    authRefresh()
      .then(({ accessToken: token }) => {
        setAuthToken(token)
        setAccessToken(token)
        return getAuthMe()
      })
      .then((userData) => {
        setUser(userData)
      })
      .catch(() => {
        // No valid refresh cookie — unauthenticated state is correct here.
        setAuthToken(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const login = async (email: string, password: string): Promise<void> => {
    const { accessToken: token, user: userData } = await authLogin(email, password)
    setAuthToken(token)
    setAccessToken(token)
    setUser(userData)
  }

  const register = async (email: string, password: string): Promise<void> => {
    const { accessToken: token, user: userData } = await authRegister(email, password)
    setAuthToken(token)
    setAccessToken(token)
    setUser(userData)
  }

  const refreshUser = async (): Promise<void> => {
    const userData = await getAuthMe()
    setUser(userData)
  }

  const logout = async (): Promise<void> => {
    try {
      await authLogout()
    } finally {
      // Always clear local state even if the server call fails.
      setAuthToken(null)
      setAccessToken(null)
      setUser(null)
    }
  }

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null && accessToken !== null,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  }

  if (isLoading) {
    return <AuthLoadingSkeleton />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be called inside <AuthProvider>')
  }
  return ctx
}
